import React, { useEffect, useState } from 'react';
import { Image, Skeleton, Typography, Space } from 'antd';
import { DownloadOutlined, RotateLeftOutlined, RotateRightOutlined, SwapOutlined, UndoOutlined, ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import FallbackImage from './fallback.png';

interface PdfFilePreviewProps {
  fileUrl: string;
  fileName: string;
  fileType?: string;
  isProcessing?: boolean;
  isShowFileName?: boolean;
}
const PDFJS_VERSION = '5.3.93';
const PDF_WORKER_URL = import.meta.env.VITE_PDF_WORKER_URL || `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.worker.min.mjs`;

const fileToExtMap: { [key: string]: string } = {
  'application/pdf': 'pdf',
  'image/jpeg': 'jpeg',
  'image/png': 'png',
  'image/jpg': 'jpg'
};

const FilePreview: React.FC<PdfFilePreviewProps> = ({ isShowFileName = true, ...props }) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);

  const pdfToImage = async (pdfUrl: string, pdfType: string): Promise<string | null> => {
    try {
     
      if (pdfUrl && pdfType != 'application/pdf') {
        return pdfUrl;
      }
  
      const { pdfjsLib } = window as any;
      pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_WORKER_URL;


      const loadingTask = pdfjsLib.getDocument(pdfUrl);
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 1.5 });

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) {
        throw new Error('Failed to get canvas context');
      }

      canvas.height = viewport.height;
      canvas.width = viewport.width;

      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };
      await page.render(renderContext).promise;

      return canvas.toDataURL();
    } catch (error) {
      console.error('Error converting PDF to image:', error);
      return null;
    }
  };
  useEffect(() => {
    const fetchImage = async () => {
      if (!props.fileUrl) {
        return;
      }
      const imageDataUrl = await pdfToImage(props.fileUrl, props.fileType ?? '');
      setImageSrc(imageDataUrl);
    };
    fetchImage();
  }, [props.fileUrl]);
  // download functionality
  const onDownload = async () => {
    if (!props.fileUrl) {
      console.error('No image source available for download');
      return;
    }

    const blob = await fetch(props.fileUrl).then((r) => r.blob());

    const blobObject = new Blob([blob], { type: props?.fileType ?? 'image/png' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blobObject);
    link.download = props.fileName?.split('/').pop() ?? 'file.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div
      style={{
        textAlign: 'center',
        fontSize: '12px',
        width: '160px'
      }}
    >
      {props.isProcessing ? (
        <Skeleton active style={{ height: '156px' }} />
      ) : (
        <Image
          preview={{
            toolbarRender: (_, { transform: { scale }, actions: { onFlipY, onFlipX, onRotateLeft, onRotateRight, onZoomOut, onZoomIn, onReset } }) => (
              <Space size={12} className="toolbar-wrapper">
                <DownloadOutlined onClick={onDownload} />
                <SwapOutlined rotate={90} onClick={onFlipY} />
                <SwapOutlined onClick={onFlipX} />
                <RotateLeftOutlined onClick={onRotateLeft} />
                <RotateRightOutlined onClick={onRotateRight} />
                <ZoomOutOutlined disabled={scale === 1} onClick={onZoomOut} />
                <ZoomInOutlined disabled={scale === 50} onClick={onZoomIn} />
                <UndoOutlined onClick={onReset} />
              </Space>
            )
          }}
          src={imageSrc ?? ''}
          alt=""
          style={{
            width: '160px',
            height: '156px',
            objectFit: 'cover',
            border: '.5px solid black'
          }}
          fallback={FallbackImage}
        />
      )}
      <div
        style={{
          textAlign: 'center'
        }}
      >
        <Typography.Text>{isShowFileName ? (!props.fileName?.includes(".") ? `${props.fileName?.split('/').pop()}.${fileToExtMap[props.fileType || '']}` : `${props.fileName?.split('/').pop()}` ): ''}</Typography.Text>
      </div>
    </div>
  );
};

export default React.memo(FilePreview);
